/**
 * Single-tab deserializer, extracted verbatim from `sessionStore.ts` (F6).
 *
 * `deserializeTab` rebuilds a runtime `Tab` from its persisted `SerializedTab`
 * form, re-adding default values for runtime-only fields. It is the shared seam
 * used both by whole-session restore (`sessionStore.loadSession`) and by
 * single-tab restore (`workspaceStore.addRestoredTab`), the latter appending one
 * deserialized tab to the current workspace without replacing the others.
 *
 * This is a mechanical move — the deserialization logic and its private coercion
 * helpers are unchanged from their original home in `sessionStore.ts`.
 */
import type { SerializedTab } from '../types/sessionTypes';
import { storageResourceRefSchema } from '../../../types/azureStorageSchema';
import type { StorageResourceRef } from '../../../types/azureStorage';
import { isWithinStorageRoot } from '../../workspace/storage-explorer/utils/storagePaths';
import { coerceEntityViewState } from '../../workspace/storage-explorer/entities/model/entityViewState';
import type { Tab } from '../../workspace/core/types/workspaceTypes';
import type { DesignerColumnRename } from '../../workspace/table-designer/types/designerSave';
import {
  createDefaultSecurityManagerContent,
  createDefaultTableDesignerContent,
  createDefaultSchemaCompareContent,
  createDefaultDataCompareContent,
  createDefaultDependencyViewerContent,
  createDefaultSchemaOverviewContent,
  createDefaultSpatialExplorerContent,
  createDefaultDataProfileContent,
  type FilterCondition,
  type FilterGroup,
  type JsonSubOperator,
  type SortDefinition,
  type DataCompareSortItem,
  type SecurityObjectType,
  type TableDesignerColumn,
  type TableDesignerOriginalState,
  type DependencyObjectType,
  type DependencyFilterState,
  type SchemaFilterMode,
  type TransactionMode,
  type TransactionIsolationLevel,
  type DataProfileDataMode,
  type DataProfileHistogramMode,
  createDefaultStartPageContent,
  createDefaultNotebookContent,
  createDefaultStorageExplorerView,
  storageExplorerModeFor,
  type StorageExplorerTabContent,
  createDefaultSessionBrowserContent,
  createDefaultTablespaceManagerContent,
  createDefaultOracleObjectDetailContent,
  createDefaultDataPumpContent,
  createDefaultInstanceMonitorContent,
  createDefaultFilegroupManagerContent,
  createDefaultPlSqlDebuggerContent,
  createDefaultPlSqlEditorContent,
  createDefaultJobSchedulerContent,
  createDefaultDashboardContent,
  createDefaultReferencesContent,
  SCHEMA_COMPARE_SCOPE_CAP_KEYS,
  type SchemaCompareScopeCapKey,
  type OracleDetailObjectKind,
  type DataImportStep,
  type DataImportColumnOverride,
  type DeadlockAnalyzerSourceKind,
  createDefaultQueryStoreContent,
  type QueryStoreTabContent,
  type DeadlockCollectorRangeFilter,
} from '../../workspace/core/types/workspaceTypes';
import type { DatabaseEngineId } from '@/core/engines';
import type { IdentifierKind, QualifiedName } from '../../navigational-features/types';
// Data Import Wizard contracts — imported from the ambient `electron.d.ts` re-export,
// NOT the electron/ path directly (see the re-export comment there for why).
import type {
  ImportParseOptions,
  ImportRowFilter,
  ImportTargetMapping,
  ImportTargetPlan,
} from '../../../types/electron';
import type { PlSqlDebuggerObjectType } from '../../workspace/pl-sql-debugger/types';
import type { HitCountMode } from '../../../types/electron';
import type { PlSqlEditorObjectType } from '../../workspace/plsql-editor/types';
import { deserializeFromSession } from '../../workspace/table-explorer/components/FkBreadcrumb/breadcrumbTree';
import {
  DEFAULT_SPATIAL_FEATURE_CAP,
  type SpatialLayerDefinition,
  type SpatialLayerGeometryRef,
  type SpatialLayerSource,
  type SpatialViewDefinition,
} from '../../workspace/spatial/types/spatialTypes';
import { SPATIAL_LAYER_PALETTE } from '../../workspace/spatial/map/mapThemes';
import { validatePolygonRing } from '../../workspace/common/utils/spatialPolygonRing';
import { clampFeatureCap } from '../../workspace/spatial-explorer/store/spatialExplorerActions';
import { resolveEngineForConnectionId } from '../../workspace/schema-compare/utils/engineUtils';
import { coerceQueryStorePositiveInt, isQueryStoreMetric } from '../../../types/queryStore';
import {
  buildOmittedCellPlaceholder,
  buildOmittedDesignerDraftMessage,
  buildOmittedScriptPlaceholder,
} from '../../../types/editorTextLimits';

// Duplicated from sessionStore — a pure environment check (no state) that keeps
// this extracted module self-contained rather than importing back into the
// store it was carved out of (which would create a circular import).
function isUnitTest(): boolean {
  if (typeof process === 'undefined' || process.env.NODE_ENV !== 'test') {
    return false;
  }
  // In Node.js (vitest), window is undefined
  if (typeof window === 'undefined') {
    return true;
  }
  // In Electron (E2E), window exists and has electronAPI
  return !window.electronAPI;
}

/**
 * Deserialize a tab for restoration.
 * Adds back default values for runtime-only fields.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A persisted character offset that still points into `text`. */
function isOffsetWithin(value: unknown, text: string): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= text.length;
}

function toRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

/** Recorded Table Designer column renames; they drive the Save migration, so a
 *  restored draft must keep them or a renamed pair becomes a refused (or, with
 *  differing types, destructive) save. Malformed entries are dropped. */
function readColumnRenames(value: unknown): DesignerColumnRename[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const renames = value.flatMap((entry): DesignerColumnRename[] =>
    isRecord(entry) && typeof entry.from === 'string' && typeof entry.to === 'string'
      ? [{ from: entry.from, to: entry.to }]
      : [],
  );
  return renames.length > 0 ? renames : undefined;
}

function coerceLimit(value: unknown): number {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value)
        : NaN;
  if (!Number.isFinite(parsed)) return 100;
  const normalized = Math.floor(parsed);
  return Math.max(1, Math.min(10000, normalized));
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

const FILTER_OPERATORS = [
  '=',
  '<',
  '<=',
  '>',
  '>=',
  'LIKE',
  'CONTAINS',
  '!=',
  'BETWEEN',
  'IN',
  'NOT IN',
  'IS NULL',
  'IS NOT NULL',
  'lookup',
  'array_contains',
  'array_not_contains',
  'array_is_empty',
  'array_is_not_empty',
  'array_length_eq',
  'array_length_neq',
  'array_length_gt',
  'array_length_lt',
  'array_length_gte',
  'array_length_lte',
  'json',
  'enum',
  'spatial',
] as const satisfies readonly FilterCondition['operator'][];

function isFilterOperator(value: unknown): value is FilterCondition['operator'] {
  return typeof value === 'string' && FILTER_OPERATORS.includes(value as FilterCondition['operator']);
}

const SPATIAL_SUB_OPERATORS = [
  'intersects_bbox',
  'within_distance',
  'intersects_polygon',
] as const satisfies readonly NonNullable<FilterCondition['spatialSubOperator']>[];

function isSpatialSubOperator(value: unknown): value is NonNullable<FilterCondition['spatialSubOperator']> {
  return (
    typeof value === 'string' &&
    SPATIAL_SUB_OPERATORS.includes(value as NonNullable<FilterCondition['spatialSubOperator']>)
  );
}

/** A tuple of `n` finite numbers, or undefined if the shape is wrong. */
function coerceNumberTuple(value: unknown, n: number): number[] | undefined {
  if (!Array.isArray(value) || value.length !== n) return undefined;
  if (!value.every((x) => typeof x === 'number' && Number.isFinite(x))) return undefined;
  return value as number[];
}

/**
 * Coerce a persisted `polygon` ring (variable-length `[lng,lat][]`) through the shared validator so
 * a hand-edited / corrupt session can't restore a self-intersecting, degenerate, antimeridian-
 * crossing, or over-200-vertex ring. Returns the canonical CCW-open ring, or undefined to drop it.
 */
function coercePolygonRing(value: unknown): [number, number][] | undefined {
  if (!Array.isArray(value) || value.length < 3) return undefined;
  const pts: [number, number][] = [];
  for (const v of value) {
    const pair = coerceNumberTuple(v, 2);
    if (!pair) return undefined;
    pts.push([pair[0], pair[1]]);
  }
  const validated = validatePolygonRing(pts);
  return validated.ok ? validated.ring : undefined;
}

/** Coerce a persisted `spatialContext` record, dropping malformed shapes. */
function coerceSpatialContext(value: unknown): FilterCondition['spatialContext'] | undefined {
  const rec = toRecord(value);
  const kind = rec.kind;
  if (kind !== 'geometry' && kind !== 'geography' && kind !== 'declared-pair') return undefined;
  const srid = rec.srid;
  // A valid SRID is null (mixed/unknown) or a finite non-negative integer — reject NaN / floats so
  // a corrupt value never reaches the mssql/mysql/oracle emit (which embed or gate on it).
  if (srid !== null && (!Number.isInteger(srid) || (srid as number) < 0)) return undefined;
  const ctx: NonNullable<FilterCondition['spatialContext']> = { kind, srid: srid as number | null };
  if (typeof rec.lngColumn === 'string') ctx.lngColumn = rec.lngColumn;
  return ctx;
}

// --- Spatial Explorer view coercion (T17) -----------------------------------
// Layer DEFINITIONS are the only thing persisted for a spatial-explorer tab, so a corrupted or
// hand-edited session JSON must never crash restore — each layer is validated independently and
// dropped (not the whole view) when malformed.

function coerceSpatialGeometryRef(value: unknown): SpatialLayerGeometryRef | null {
  const r = toRecord(value);
  if (r.kind === 'column' && typeof r.column === 'string') {
    return { kind: 'column', column: r.column };
  }
  if (r.kind === 'declaration' && typeof r.declarationId === 'string') {
    return { kind: 'declaration', declarationId: r.declarationId };
  }
  return null;
}

function coerceSpatialLayerSource(value: unknown): SpatialLayerSource | null {
  const r = toRecord(value);
  const geometryRef = coerceSpatialGeometryRef(r.geometryRef);
  if (!geometryRef) return null;
  if (r.kind === 'table' && typeof r.schema === 'string' && typeof r.table === 'string') {
    return { kind: 'table', schema: r.schema, table: r.table, geometryRef };
  }
  if (r.kind === 'query' && typeof r.sql === 'string') {
    // `sqlOmitted` travels with the (empty) sql so `loadQuerySourceLayer` can
    // refuse to run the layer instead of executing '' and reporting a generic
    // "Query failed" for a query we dropped ourselves.
    return {
      kind: 'query',
      sql: r.sql,
      geometryRef,
      ...(r.sqlOmitted === true ? { sqlOmitted: true } : {}),
    };
  }
  return null;
}

function coerceSpatialLayer(value: unknown): SpatialLayerDefinition | null {
  const r = toRecord(value);
  const source = coerceSpatialLayerSource(r.source);
  if (!source || typeof r.id !== 'string' || typeof r.label !== 'string') return null;
  const styleRecord = toRecord(r.style);
  return {
    id: r.id,
    label: r.label,
    visible: typeof r.visible === 'boolean' ? r.visible : true,
    source,
    ...(typeof r.labelColumn === 'string' ? { labelColumn: r.labelColumn } : {}),
    style: {
      color: typeof styleRecord.color === 'string' ? styleRecord.color : SPATIAL_LAYER_PALETTE[0],
      ...(typeof styleRecord.colorByColumn === 'string' ? { colorByColumn: styleRecord.colorByColumn } : {}),
      ...(typeof styleRecord.cluster === 'boolean' ? { cluster: styleRecord.cluster } : {}),
    },
    featureCap: typeof r.featureCap === 'number' ? clampFeatureCap(r.featureCap) : DEFAULT_SPATIAL_FEATURE_CAP,
  };
}

/**
 * Sane ceiling for a restored spatial view (Checkpoint-4 LOW-d). Hand-edited/corrupted session JSON
 * could otherwise carry an unbounded layer array — activating that tab would fire one fetch per
 * layer (a fetch storm). No feature currently offers a per-tab cap of its own, so this is the only
 * bound; overflow layers are dropped, never a failed/crashed tab restore.
 */
export const MAX_RESTORED_SPATIAL_LAYERS = 50;

function coerceSpatialView(value: unknown): SpatialViewDefinition {
  const r = toRecord(value);
  const layers: SpatialLayerDefinition[] = [];
  if (Array.isArray(r.layers)) {
    const seenIds = new Set<string>();
    for (const l of r.layers) {
      if (layers.length >= MAX_RESTORED_SPATIAL_LAYERS) break;
      const layer = coerceSpatialLayer(l);
      if (!layer) continue;
      // Duplicate ids (hand-edited JSON) would collide as React keys and mount two loaders for the
      // same id — keep only the first occurrence.
      if (seenIds.has(layer.id)) continue;
      seenIds.add(layer.id);
      layers.push(layer);
    }
  }
  const basemap = r.basemap === 'none' ? 'none' : 'default';
  return { layers, basemap };
}

// Runtime list blueprints the JsonSubOperator union — keep `as const satisfies`
// so the type checker errors out when the union gains a new member.
const JSON_SUB_OPERATORS = [
  'has_property',
  'not_has_property',
  'path_eq',
  'path_ieq',
  'path_neq',
  'path_is_null',
  'path_is_not_null',
  'path_contains',
  'path_gt',
  'path_gte',
  'path_lt',
  'path_lte',
  'is_empty',
  'is_not_empty',
  'is_valid',
  'is_not_valid',
  'any_eq',
  'any_ieq',
  'any_contains',
  'any_gt',
  'any_gte',
  'any_lt',
  'any_lte',
  'all_eq',
  'all_gt',
  'all_gte',
  'all_lt',
  'all_lte',
  'property_lookup',
  'any_lookup',
  'all_lookup',
  'property_enum',
  'any_enum',
  'all_enum',
] as const satisfies readonly JsonSubOperator[];

function isJsonSubOperator(value: unknown): value is JsonSubOperator {
  return typeof value === 'string' && JSON_SUB_OPERATORS.includes(value as JsonSubOperator);
}

function coerceSelectedColumns(value: unknown): string[] | '*' {
  if (value === '*') return '*';
  return isStringArray(value) ? value : '*';
}

function coerceSortDefinitions(value: unknown): SortDefinition[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    const record = toRecord(item);
    const field = typeof record.field === 'string' ? record.field : null;
    const direction = record.direction === 'asc' || record.direction === 'desc' ? record.direction : null;
    if (!field || !direction) return [];
    return [{ field, direction }];
  });
}

// F07: Data Compare results sorting uses `columnId` (not `field` — it can
// reference the synthetic `__status` diff-type column), so it needs its own
// coercion rather than reusing coerceSortDefinitions.
function coerceDataCompareSorting(value: unknown): DataCompareSortItem[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    const record = toRecord(item);
    const columnId = typeof record.columnId === 'string' ? record.columnId : null;
    const direction = record.direction === 'asc' || record.direction === 'desc' ? record.direction : null;
    if (!columnId || !direction) return [];
    return [{ columnId, direction }];
  });
}

function coerceFilterGroups(value: unknown): FilterGroup[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((groupValue) => {
    const record = toRecord(groupValue);
    const field = typeof record.field === 'string' ? record.field : null;
    if (!field) return [];

    const conditionsValue = record.conditions;
    const conditions: FilterCondition[] = Array.isArray(conditionsValue)
      ? conditionsValue.flatMap((conditionValue) => {
          const conditionRecord = toRecord(conditionValue);
          const operator = conditionRecord.operator;
          if (!isFilterOperator(operator)) return [];

          const condition: FilterCondition = {
            operator,
          };

          if ('value' in conditionRecord) {
            condition.value = conditionRecord.value as FilterCondition['value'];
          }
          if ('value2' in conditionRecord) {
            condition.value2 = conditionRecord.value2 as FilterCondition['value2'];
          }
          // Invalid jsonSubOperator strings are dropped silently — the condition
          // survives without the sub-op rather than the whole condition vanishing.
          if (isJsonSubOperator(conditionRecord.jsonSubOperator)) {
            condition.jsonSubOperator = conditionRecord.jsonSubOperator;
          }
          if (typeof conditionRecord.path === 'string') {
            condition.path = conditionRecord.path;
          }
          if (typeof conditionRecord.jsonDeclaredOnText === 'boolean') {
            condition.jsonDeclaredOnText = conditionRecord.jsonDeclaredOnText;
          }
          if (typeof conditionRecord.sourcePath === 'string') {
            condition.sourcePath = conditionRecord.sourcePath;
          }
          // Spatial fields (bbox / distance / draw-area chips). Without these in the
          // allowlist a restored spatial chip would silently lose its operator
          // and vanish. Malformed shapes are dropped field-by-field.
          if (isSpatialSubOperator(conditionRecord.spatialSubOperator)) {
            condition.spatialSubOperator = conditionRecord.spatialSubOperator;
          }
          const bbox = coerceNumberTuple(conditionRecord.bbox, 4);
          if (bbox) condition.bbox = bbox as [number, number, number, number];
          const point = coerceNumberTuple(conditionRecord.point, 2);
          if (point) condition.point = point as [number, number];
          if (typeof conditionRecord.distanceMeters === 'number' && Number.isFinite(conditionRecord.distanceMeters)) {
            condition.distanceMeters = conditionRecord.distanceMeters;
          }
          const polygon = coercePolygonRing(conditionRecord.polygon);
          if (polygon) condition.polygon = polygon;
          const spatialContext = coerceSpatialContext(conditionRecord.spatialContext);
          if (spatialContext) condition.spatialContext = spatialContext;

          return [condition];
        })
      : [];

    return [{ field, conditions }];
  });
}

/** Validates a persisted collector time filter (F1-B); anything unexpected restores as the default. */
function parseDeadlockCollectorRange(raw: unknown): DeadlockCollectorRangeFilter | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const record = raw as Record<string, unknown>;
  if (record.kind === 'last24h' || record.kind === 'last7d') return { kind: record.kind };
  if (record.kind === 'custom' && typeof record.fromUtc === 'string' && typeof record.toUtc === 'string') {
    return { kind: 'custom', fromUtc: record.fromUtc, toUtc: record.toUtc };
  }
  return undefined;
}

/**
 * Azure Storage (spec §6.1) — a persisted ref is validated with the shared zod
 * schema; a malformed `resource` degrades to the root, a malformed `root` to
 * an INERT account root (the tab renders "could not be restored · Close" and
 * never lists — no broader root is invented and fetched). `mode` is
 * re-derived from the root; the nested `view` is normalised field by field.
 */
function deserializeStorageExplorerContent(contentRecord: Record<string, unknown>): StorageExplorerTabContent {
  const connectionId = typeof contentRecord.connectionId === 'string' ? contentRecord.connectionId : '';
  const parsedRoot = storageResourceRefSchema.safeParse(contentRecord.root);
  const root: StorageResourceRef = parsedRoot.success ? parsedRoot.data : { kind: 'account' };
  const parsedResource = storageResourceRefSchema.safeParse(contentRecord.resource);
  const resource: StorageResourceRef = parsedResource.success && isWithinStorageRoot(root, parsedResource.data) ? parsedResource.data : root;
  const view = toRecord(contentRecord.view);
  const defaults = createDefaultStorageExplorerView();
  const entityView = toRecord(contentRecord.entityView);
  const messageView = toRecord(contentRecord.messageView);
  return {
    connectionId,
    root,
    resource,
    mode: storageExplorerModeFor(root),
    view: {
      listing: view.listing === 'flat' ? 'flat' : defaults.listing,
      prefixFilter: typeof view.prefixFilter === 'string' ? view.prefixFilter : defaults.prefixFilter,
      showSnapshots: view.showSnapshots === true,
      showVersions: view.showVersions === true,
      showDeleted: view.showDeleted === true,
    },
    ...(contentRecord.entityView && typeof contentRecord.entityView === 'object'
      ? {
          entityView: coerceEntityViewState(entityView),
        }
      : {}),
    ...(contentRecord.messageView && typeof contentRecord.messageView === 'object'
      ? {
          messageView: {
            peekCount: typeof messageView.peekCount === 'number' && messageView.peekCount > 0 ? messageView.peekCount : 32,
            decodeBase64: messageView.decodeBase64 === true,
          },
        }
      : {}),
    restoredFromSession: true,
  };
}

/**
 * A retired separate Visual Query Editor tab (snapshots from older versions)
 * restores as a Query tab in its Visual view on the same statement, keeping its
 * id, title and connection. It is never merged into the tab it was opened from:
 * `sourceQueryTabId` is dropped. An omitted buffer restores like a query tab's.
 */
function migrateVisualQueryTab(serialized: SerializedTab): SerializedTab {
  const record = toRecord(serialized.content);
  return {
    ...serialized,
    type: 'query',
    content: {
      query: typeof record.query === 'string' ? record.query : '',
      ...(record.queryOmitted === true ? { queryOmitted: true } : {}),
      viewMode: 'visual',
      ...(typeof record.statementOffset === 'number' ? { visualStatementOffset: record.statementOffset } : {}),
    },
  };
}

export function deserializeTab(serialized: SerializedTab): Tab {
  if (serialized.type === 'visual-query') return deserializeTab(migrateVisualQueryTab(serialized));
  const sourcePin =
    typeof serialized.sourcePin?.connectionId === 'string' &&
    serialized.sourcePin.connectionId !== '' &&
    typeof serialized.sourcePin?.pinId === 'string' &&
    serialized.sourcePin.pinId !== ''
      ? { connectionId: serialized.sourcePin.connectionId, pinId: serialized.sourcePin.pinId }
      : undefined;

  const base = {
    id: serialized.id,
    title: serialized.title,
    customTitle: serialized.customTitle,
    connectionInfo: serialized.connectionInfo,
    ...(sourcePin ? { sourcePin } : {}),
  };

  const contentRecord = toRecord(serialized.content);

  if (serialized.type === 'query') {
    const persistedFilePath = typeof contentRecord.filePath === 'string' ? contentRecord.filePath : undefined;
    // A body omitted at save time (too large to persist) restores DETACHED
    // from its file: the placeholder comment is not the script, so the tab
    // must never carry `filePath` (Save would overwrite the real file) or a
    // saved hash. A file-tied tab schedules `pendingFileReload` instead —
    // QueryEditorTab re-reads the file on mount and re-attaches on success.
    const queryOmitted = contentRecord.queryOmitted === true;
    const query = queryOmitted
      ? buildOmittedScriptPlaceholder(persistedFilePath)
      : typeof contentRecord.query === 'string' ? contentRecord.query : '';
    const filePath = queryOmitted ? undefined : persistedFilePath;
    const fileLastSavedHash = !queryOmitted && typeof contentRecord.fileLastSavedHash === 'string'
      ? contentRecord.fileLastSavedHash
      : undefined;
    const pendingFileReload = queryOmitted && persistedFilePath ? persistedFilePath : undefined;
    // chartConfigs intentionally NOT restored — they reference result columns that
    // don't exist after session restore. Chart panel shows empty state.
    const transactionMode = isTransactionMode(contentRecord.transactionMode)
      ? contentRecord.transactionMode
      : 'auto';
    const isolationLevel = isTransactionIsolationLevel(contentRecord.isolationLevel)
      ? contentRecord.isolationLevel
      : 'default';

    const aiChatSessionId = typeof contentRecord.aiChatSessionId === 'string' ? contentRecord.aiChatSessionId : undefined;
    const aiChatSidebarOpen = contentRecord.aiChatSidebarOpen === true;
    // A legacy `fileResolutionMode` field in old session files is intentionally
    // dropped: script-scope completion is always on now.
    // An omitted body restores in SQL view: the placeholder comment explains
    // the gap there, while the Visual view would show it as "no statement".
    const visualView = contentRecord.viewMode === 'visual' && !queryOmitted
      ? {
          viewMode: 'visual' as const,
          visualStatementOffset: isOffsetWithin(contentRecord.visualStatementOffset, query) ? contentRecord.visualStatementOffset : null,
        }
      : {};

    return {
      ...base,
      type: 'query',
      content: {
        query,
        filePath,
        fileLastSavedHash,
        ...(pendingFileReload ? { pendingFileReload } : {}),
        resultSets: [],
        messages: [],
        results: [],
        columns: [],
        // Restore preferences, always reset state to idle
        transactionMode,
        transactionState: 'idle',
        isolationLevel,
        aiChatSessionId,
        aiChatSidebarOpen: aiChatSidebarOpen || undefined,
        ...visualView,
      },
    };
  }

  if (serialized.type === 'table-editor') {
    return {
      ...base,
      type: 'table-editor',
      content: {
        tableName: typeof contentRecord.tableName === 'string' ? contentRecord.tableName : '',
        schema: typeof contentRecord.schema === 'string' ? contentRecord.schema : 'dbo',
        filters: [],
        pendingChanges: [],
        rows: [],
        columns: [],
      },
    };
  }

  if (serialized.type === 'table-explorer') {
    // Restore filters and sorting from serialized content, with fallbacks for migration
    // (sessions saved before filters/sorting were persisted won't have these fields)
    const tableName = typeof contentRecord.tableName === 'string' ? contentRecord.tableName : '';
    const schema = typeof contentRecord.schema === 'string' ? contentRecord.schema : 'dbo';
    const database = typeof contentRecord.database === 'string' ? contentRecord.database : '';
    const limit = coerceLimit(contentRecord.limit);
    const selectedColumns = coerceSelectedColumns(contentRecord.selectedColumns);
    const filters = coerceFilterGroups(contentRecord.filters);
    const sorting = coerceSortDefinitions(contentRecord.sorting);
    // Slice 6 (FK Breadcrumb) — best-effort restore of the navigation tree;
    // unsupported PK shapes get pruned out by `deserializeFromSession`.
    const navigationTree = deserializeFromSession(contentRecord.navigationTree);

    return {
      ...base,
      type: 'table-explorer',
      content: {
        tableName,
        schema,
        database,
        limit,
        selectedColumns,
        filters,
        sorting,
        rows: [],
        columns: [],
        isLoading: false,
        isEditMode: false,
        pendingChanges: [],
        primaryKeyColumns: [],
        restoredFromSession: true,
        // K2: `pendingColumnReconcile` is a runtime-only flag (never serialized).
        // A linked pin tab saved before its column-reconcile completed would
        // restore ungated and could run the pinned data query with stale column
        // references. Re-derive the gate for a restored tab that carries a
        // `sourcePin` so it re-reconciles against the live schema on next open.
        // Ordinary (non-pin) tabs have no sourcePin and are untouched.
        //
        // L3/M1: ONLY when the tab is at ROOT. A tab restored mid-FK-drill
        // (active node ≠ root) fetches metadata for the ACTIVE (child) table
        // while reconciliation mutates ROOT filters/sorting/selectedColumns —
        // gating it would reconcile root fields against child metadata, so it
        // skips the gate (its root settings were validated at pin-open time).
        // Root-level means: no tree at all, OR a tree whose active node IS the
        // root (M1 — a drilled-then-returned-to-root tab keeps a truthy tree
        // with `activeNodeId === rootId`; treating any truthy tree as non-root
        // would let it run stale root column references ungated). The check
        // runs on the `deserializeFromSession`-validated tree, whose string
        // ids are guaranteed — and which re-points a pruned active node back
        // to the root, so this reflects the true post-restore state. Pinned
        // payloads never carry a navigationTree, so fresh pin-opens are
        // unaffected.
        ...(sourcePin && (!navigationTree || navigationTree.activeNodeId === navigationTree.rootId)
          ? { pendingColumnReconcile: true }
          : {}),
        ...(navigationTree ? { navigationTree } : {}),
      },
    };
  }

  if (serialized.type === 'plan') {
    return {
      ...base,
      type: 'plan',
      content: {
        view: contentRecord.view === 'compare' ? 'compare' : 'single',
        filePath: typeof contentRecord.filePath === 'string' ? contentRecord.filePath : undefined,
        baselineFilePath: typeof contentRecord.baselineFilePath === 'string' ? contentRecord.baselineFilePath : undefined,
        compareFilePath: typeof contentRecord.compareFilePath === 'string' ? contentRecord.compareFilePath : undefined,
      },
    };
  }

  if (serialized.type === 'table-designer') {
    const mode = contentRecord.mode === 'alter' ? 'alter' : 'create';
    const database = typeof contentRecord.database === 'string' ? contentRecord.database : '';
    const schema = typeof contentRecord.schema === 'string' ? contentRecord.schema : 'dbo';
    const tableName = typeof contentRecord.tableName === 'string' ? contentRecord.tableName : '';

    const baseContent = createDefaultTableDesignerContent(mode, database, schema, tableName);
    // "A capped tab is never a legacy draft" is enforced by the capper, which
    // drops `originalState` and empties `columns`. Enforced here as well, on
    // the way back in: an invariant that lives in exactly one place is how the
    // legacy-resurrection bug got in, and a hand-edited or older snapshot can
    // still claim `sqlOmitted` while carrying legacy fields — which would put
    // a buffer synthesized from stale form-state edits in front of Save.
    const sqlOmitted = contentRecord.sqlOmitted === true;

    return {
      ...base,
      type: 'table-designer',
      content: {
        ...baseContent,
        columns: sqlOmitted || !Array.isArray(contentRecord.columns)
          ? []
          : (contentRecord.columns as TableDesignerColumn[]),
        originalState: !sqlOmitted && isRecord(contentRecord.originalState)
          ? (contentRecord.originalState as TableDesignerOriginalState)
          : undefined,
        // A buffer omitted at save time restores as `undefined`, NEVER as a
        // placeholder: Save diffs `currentSql` against `initialSql` and runs
        // the result as DDL. `TableDesignerTab`'s bootstrap effect re-derives
        // the buffer from the baseline (alter) or the blank template (create),
        // which lands on "no changes to save"; the banner below is the only
        // thing that tells the user their draft is gone.
        initialSql: typeof contentRecord.initialSql === 'string' ? contentRecord.initialSql : undefined,
        currentSql: typeof contentRecord.currentSql === 'string' ? contentRecord.currentSql : undefined,
        columnRenames: readColumnRenames(contentRecord.columnRenames),
        viewSqlMode: contentRecord.viewSqlMode === true,
        isDirty: contentRecord.isDirty === true,
        isLoading: false,
        isApplying: false,
        // The flag outlives the banner's first render: it is what tells the
        // next snapshot to keep saying "omitted" (see
        // `isUntouchedOmittedDesignerDraft`) and what gives the disabled Save
        // button a reason. `onBufferChange` clears both on the first edit.
        ...(sqlOmitted ? { sqlOmitted: true } : {}),
        error: sqlOmitted ? buildOmittedDesignerDraftMessage() : undefined,
      },
    };
  }

  if (serialized.type === 'security') {
    const view = contentRecord.view === 'database' ? 'database' : 'server';
    const objectType = isSecurityObjectType(contentRecord.objectType)
      ? (contentRecord.objectType as SecurityObjectType)
      : view === 'server'
        ? 'login'
        : 'user';
    const selectedDatabase = typeof contentRecord.selectedDatabase === 'string' ? contentRecord.selectedDatabase : undefined;

    const baseContent = createDefaultSecurityManagerContent(view, objectType, selectedDatabase);

    return {
      ...base,
      type: 'security',
      content: {
        ...baseContent,
        selectedObjectId: undefined,
        isCreateMode: false,
      },
    };
  }

  if (serialized.type === 'schema-compare') {
    const source = isRecord(contentRecord.source) ? contentRecord.source : undefined;
    const target = isRecord(contentRecord.target) ? contentRecord.target : undefined;
    // W5.2 fix round 1 (F-3): pass the engine through. Sessions saved before
    // compare options were persisted restore with the factory baseline, and a
    // baseline built with no engine carries a cross-engine ignore list that
    // `inferDepth` reports as 'custom' once the tab resolves its real engine.
    const restoredEngine =
      resolveEngineForConnectionId(
        typeof source?.connectionId === 'string' ? source.connectionId : undefined
      ) ??
      resolveEngineForConnectionId(
        typeof target?.connectionId === 'string' ? target.connectionId : undefined
      );
    const baseContent = createDefaultSchemaCompareContent(
      source as { connectionId?: string; database?: string } | undefined,
      target as { connectionId?: string; database?: string } | undefined,
      restoredEngine
    );
    const options = isRecord(contentRecord.options) ? contentRecord.options : undefined;
    // W3.4/F07 fix round 4 — the blueprint scope-cap marker. Filtered against
    // the known key list because this comes off disk as JSON: an unknown string
    // would be handed to `pickInheritedCaps`, which indexes `options` with it
    // and would silently mark a `undefined` value as an imposed cap. Absent
    // stays absent (a session written before the marker existed keeps the
    // endpoint-kind fallback — but only for its first confirm through the
    // dialog after restore; `narrowScopeCapKeys` always writes a defined array
    // from then on, per its own comment, so a second Change never revisits the
    // heuristic); an empty array is preserved as `[]`, which means something
    // quite different — see `SerializedSchemaCompareTabContent`.
    const scopeCapKeys = Array.isArray(contentRecord.scopeCapKeys)
      ? contentRecord.scopeCapKeys.filter((key): key is SchemaCompareScopeCapKey =>
          (SCHEMA_COMPARE_SCOPE_CAP_KEYS as readonly unknown[]).includes(key)
        )
      : undefined;

    return {
      ...base,
      type: 'schema-compare',
      content: {
        ...baseContent,
        options: (options as typeof baseContent.options | undefined) ?? baseContent.options,
        // F07: restore compareWholeDatabase when the session declared it;
        // sessions saved before this was persisted fall back to
        // baseContent's default (true) — no migration needed.
        compareWholeDatabase:
          typeof contentRecord.compareWholeDatabase === 'boolean'
            ? contentRecord.compareWholeDatabase
            : baseContent.compareWholeDatabase,
        ...(scopeCapKeys ? { scopeCapKeys } : {}),
        // #98: "Wrap in a single transaction". Restored only when the session
        // actually declared it — absent must stay absent so the tab falls back
        // to the engine-derived default (ON for SQL Server), which is what a
        // session written before this field existed should get. A `?? false`
        // here would turn every pre-#98 session into a permanent opt-out.
        ...(typeof contentRecord.wrapScriptInTransaction === 'boolean'
          ? { wrapScriptInTransaction: contentRecord.wrapScriptInTransaction }
          : {}),
        // Diff results are not persisted - mark as needing refresh
        needsRefresh: true,
      },
    };
  }

  if (serialized.type === 'data-compare') {
    // Migrate persisted endpoints (which historically had no `kind` field)
    // into the discriminated `DataCompareConnectionEndpoint` shape.
    const rehydrateEndpoint = (rec: Record<string, unknown> | undefined) => {
      if (!rec) return undefined;
      const existingKind = typeof rec.kind === 'string' ? rec.kind : undefined;
      if (existingKind === 'blueprint-folder') {
        return {
          kind: 'blueprint-folder' as const,
          folderPath: typeof rec.folderPath === 'string' ? rec.folderPath : '',
          schema: typeof rec.schema === 'string' ? rec.schema : '',
          table: typeof rec.table === 'string' ? rec.table : '',
        };
      }
      return {
        kind: 'connection' as const,
        connectionId: typeof rec.connectionId === 'string' ? rec.connectionId : '',
        database: typeof rec.database === 'string' ? rec.database : '',
        schema: typeof rec.schema === 'string' ? rec.schema : '',
        table: typeof rec.table === 'string' ? rec.table : '',
      };
    };
    const source = rehydrateEndpoint(
      isRecord(contentRecord.source) ? contentRecord.source : undefined,
    );
    const target = rehydrateEndpoint(
      isRecord(contentRecord.target) ? contentRecord.target : undefined,
    );
    const baseContent = createDefaultDataCompareContent(source, target);
    const options = isRecord(contentRecord.options) ? contentRecord.options : undefined;

    return {
      ...base,
      type: 'data-compare',
      content: {
        ...baseContent,
        keyMappings: Array.isArray(contentRecord.keyMappings)
          ? (contentRecord.keyMappings as Array<{ sourceColumn: string; targetColumn: string }>)
          : [],
        valueMappings: Array.isArray(contentRecord.valueMappings)
          ? (contentRecord.valueMappings as Array<{ sourceColumn: string; targetColumn: string; included: boolean }>)
          : [],
        options: (options as typeof baseContent.options | undefined) ?? baseContent.options,
        // F07: restore results filters + sorting; sessions saved before these
        // were persisted fall back to `[]`, same as a brand-new tab.
        filters: coerceFilterGroups(contentRecord.filters),
        sorting: coerceDataCompareSorting(contentRecord.sorting),
        // Diff results are not persisted - mark as needing refresh
        needsRefresh: true,
      },
    };
  }

  if (serialized.type === 'dependency-viewer') {
    const objectName = typeof contentRecord.objectName === 'string' ? contentRecord.objectName : '';
    const objectType = isDependencyObjectType(contentRecord.objectType)
      ? (contentRecord.objectType as DependencyObjectType)
      : 'table';
    const schema = typeof contentRecord.schema === 'string' ? contentRecord.schema : '';
    const database = typeof contentRecord.database === 'string' ? contentRecord.database : '';
    // B7 added a third mode; coercing anything non-'tree' to 'graph' would
    // silently drop a restored find-usages tab back to the hairball.
    const viewMode =
      contentRecord.viewMode === 'tree'
        ? 'tree'
        : contentRecord.viewMode === 'find-usages'
          ? 'find-usages'
          : 'graph';
    const findUsagesTerm =
      typeof contentRecord.findUsagesTerm === 'string' && contentRecord.findUsagesTerm.trim()
        ? contentRecord.findUsagesTerm
        : undefined;

    const baseContent = createDefaultDependencyViewerContent(objectName, objectType, schema, database);

    return {
      ...base,
      type: 'dependency-viewer',
      content: {
        ...baseContent,
        viewMode,
        filterState: (isRecord(contentRecord.filterState) ? contentRecord.filterState : undefined) as DependencyFilterState | undefined,
        // Only the term comes back; the scan re-runs on first render.
        ...(findUsagesTerm ? { findUsages: { searchTerm: findUsagesTerm } } : {}),
        navigationStack: [],
        // Dependencies will be re-fetched on load
        isLoading: true,
      },
    };
  }

  if (serialized.type === 'schema-overview') {
    const connectionId = typeof contentRecord.connectionId === 'string' ? contentRecord.connectionId : '';
    const databaseName = typeof contentRecord.databaseName === 'string' ? contentRecord.databaseName : '';
    const filterMode = isSchemaFilterMode(contentRecord.filterMode)
      ? (contentRecord.filterMode as SchemaFilterMode)
      : 'all';

    const baseContent = createDefaultSchemaOverviewContent(connectionId, databaseName, filterMode);

    return {
      ...base,
      type: 'schema-overview',
      content: {
        ...baseContent,
        // Set to dormant state - user must explicitly activate
        isLoading: false,
        isDormant: true,
      },
    };
  }

  if (serialized.type === 'data-profile') {
    const connectionId = typeof contentRecord.connectionId === 'string' ? contentRecord.connectionId : '';
    const database = typeof contentRecord.database === 'string' ? contentRecord.database : '';
    const schema = typeof contentRecord.schema === 'string' ? contentRecord.schema : '';
    const table = typeof contentRecord.table === 'string' ? contentRecord.table : '';
    const focusedColumn = typeof contentRecord.focusedColumn === 'string' ? contentRecord.focusedColumn : undefined;
    const dataMode: DataProfileDataMode =
      contentRecord.dataMode === 'sample' ? 'sample' : 'server';
    const histogramMode: DataProfileHistogramMode =
      contentRecord.histogramMode === 'quantile' ? 'quantile' : 'equal-width';

    const baseContent = createDefaultDataProfileContent({
      connectionId,
      database,
      schema,
      table,
      focusedColumn,
      dataMode,
    });

    return {
      ...base,
      type: 'data-profile',
      content: {
        ...baseContent,
        histogramMode,
        ...(typeof contentRecord.sampleSize === 'number' ? { sampleSize: contentRecord.sampleSize } : {}),
        ...(typeof contentRecord.bucketCount === 'number' ? { bucketCount: contentRecord.bucketCount } : {}),
        ...(typeof contentRecord.topN === 'number' ? { topN: contentRecord.topN } : {}),
        filters: coerceFilterGroups(contentRecord.filters),
        ...(typeof contentRecord.rowLimit === 'number' ? { rowLimit: contentRecord.rowLimit } : {}),
        ...(contentRecord.hideLargeTableHint === true ? { hideLargeTableHint: true } : {}),
        // Sample rows and aggregation results aren't persisted — wait for user to click Reload.
        isLoading: false,
        isDormant: true,
      },
    };
  }

  if (serialized.type === 'start-page') {
    const baseContent = createDefaultStartPageContent();
    const expandedSections = isRecord(contentRecord.expandedSections) ? contentRecord.expandedSections : undefined;
    const selectedConnectionId =
      contentRecord.selectedConnectionId === null || typeof contentRecord.selectedConnectionId === 'string'
        ? (contentRecord.selectedConnectionId as string | null)
        : null;

    return {
      ...base,
      type: 'start-page',
      content: {
        ...baseContent,
        expandedSections: expandedSections ? (expandedSections as typeof baseContent.expandedSections) : baseContent.expandedSections,
        tabSearchQuery: typeof contentRecord.tabSearchQuery === 'string' ? contentRecord.tabSearchQuery : '',
        selectedConnectionId,
      },
    };
  }

  if (serialized.type === 'notebook') {
    const baseContent = createDefaultNotebookContent();
    const rawCells = Array.isArray(contentRecord.cells) ? contentRecord.cells : [];
    const persistedNotebookPath = typeof contentRecord.filePath === 'string' ? contentRecord.filePath : undefined;
    // A cell source omitted at save time (too large to persist) comes back as
    // an explanatory placeholder, never as an empty cell — and the notebook
    // restores DETACHED from its file (below), because the placeholder is not
    // the user's content and Save would write it over the real `.ipynb`.
    // Unlike a query tab there is no reload-on-mount for notebooks, so the
    // placeholder names the path the user can reopen.
    let anySourceOmitted = false;
    const cells = rawCells
      .filter((c): c is Record<string, unknown> => isRecord(c))
      .map((c) => {
        const id = typeof c.id === 'string' ? c.id : crypto.randomUUID();
        const sourceOmitted = c.sourceOmitted === true;
        if (sourceOmitted) anySourceOmitted = true;
        const cellType = c.cellType === 'markdown' ? 'markdown' as const
          : c.cellType === 'javascript' ? 'javascript' as const
          : 'code' as const;
        const source = sourceOmitted
          ? buildOmittedCellPlaceholder(cellType, persistedNotebookPath)
          : typeof c.source === 'string' ? c.source : '';
        const chartConfig = c.chartConfig && typeof c.chartConfig === 'object' ? c.chartConfig as import('../../workspace/charts/types/chartTypes').ChartConfig : undefined;
        if (cellType === 'markdown') {
          return { id, cellType, source, isEditing: false };
        }
        if (cellType === 'javascript') {
          return { id, cellType, source, executionState: 'idle' as const, ...(chartConfig ? { chartConfig } : {}) };
        }
        // Default to code cell; reset execution state
        return { id, cellType, source, executionState: 'idle' as const, ...(chartConfig ? { chartConfig } : {}) };
      });
    const metadata = isRecord(contentRecord.metadata)
      ? { ...baseContent.metadata, ...(contentRecord.metadata as Record<string, unknown>) }
      : baseContent.metadata;

    const aiChatSessionId = typeof contentRecord.aiChatSessionId === 'string' ? contentRecord.aiChatSessionId : undefined;
    const aiChatSidebarOpen = contentRecord.aiChatSidebarOpen === true;

    // A detached notebook loses its pinned-workspace link too. `findLinkedTabs`
    // matches a tab to a pin on `sourcePin` ALONE — `filePath` is never
    // consulted — so a placeholder tab that kept its `sourcePin` would still be
    // found by a later "open pin": the pin's notebook is re-read, this tab is
    // not dirty, and `revalidateNotebookTab` replaces its cells with the pin's
    // and stamps the pin's hash — over whatever file the user has since saved
    // this tab to. Save As cannot rescue it either (the G4 sever needs a
    // PREVIOUS `filePath`, and a detached tab has none). A placeholder is not
    // any pinned definition's origin, so the link goes with the file binding.
    const { sourcePin: _detachedSourcePin, ...baseWithoutSourcePin } = base;
    void _detachedSourcePin;

    return {
      ...(anySourceOmitted ? baseWithoutSourcePin : base),
      type: 'notebook',
      content: {
        cells: cells.length > 0 ? cells : baseContent.cells,
        metadata: metadata as typeof baseContent.metadata,
        executionCounter: 0,
        isDirty: false,
        filePath: anySourceOmitted ? undefined : persistedNotebookPath,
        lastSavedHash: anySourceOmitted || typeof contentRecord.lastSavedHash !== 'string'
          ? undefined
          : contentRecord.lastSavedHash,
        aiChatSessionId,
        aiChatSidebarOpen: aiChatSidebarOpen || undefined,
      },
    };
  }

  if (serialized.type === 'session-browser') {
    return {
      ...base,
      type: 'session-browser',
      content: createDefaultSessionBrowserContent(),
    };
  }

  if (serialized.type === 'tablespace-manager') {
    return {
      ...base,
      type: 'tablespace-manager',
      content: createDefaultTablespaceManagerContent(),
    };
  }

  if (serialized.type === 'oracle-object-detail') {
    const objectName = typeof contentRecord.objectName === 'string' ? contentRecord.objectName : '';
    const schema = typeof contentRecord.schema === 'string' ? contentRecord.schema : '';
    const database = typeof contentRecord.database === 'string' ? contentRecord.database : '';
    const VALID_KINDS: OracleDetailObjectKind[] = ['sequence', 'synonym', 'db-link', 'materialized-view', 'type'];
    const objectKind: OracleDetailObjectKind = VALID_KINDS.includes(contentRecord.objectKind as OracleDetailObjectKind)
      ? (contentRecord.objectKind as OracleDetailObjectKind)
      : 'sequence';
    return {
      ...base,
      type: 'oracle-object-detail',
      content: createDefaultOracleObjectDetailContent(objectName, schema, database, objectKind),
    };
  }

  if (serialized.type === 'data-pump') {
    return {
      ...base,
      type: 'data-pump',
      content: createDefaultDataPumpContent(),
    };
  }

  if (serialized.type === 'instance-monitor') {
    return {
      ...base,
      type: 'instance-monitor',
      content: createDefaultInstanceMonitorContent(),
    };
  }

  if (serialized.type === 'filegroup-manager') {
    return {
      ...base,
      type: 'filegroup-manager',
      content: createDefaultFilegroupManagerContent(),
    };
  }

  if (serialized.type === 'pl-sql-debugger') {
    const objectName = typeof contentRecord.objectName === 'string' ? contentRecord.objectName : '';
    const schema = typeof contentRecord.schema === 'string' ? contentRecord.schema : '';
    const database = typeof contentRecord.database === 'string' ? contentRecord.database : '';
    const VALID_TYPES: PlSqlDebuggerObjectType[] = ['procedure', 'function', 'package', 'trigger', 'anonymous'];
    const objectType: PlSqlDebuggerObjectType = VALID_TYPES.includes(contentRecord.objectType as PlSqlDebuggerObjectType)
      ? (contentRecord.objectType as PlSqlDebuggerObjectType)
      : 'procedure';
    // An anonymous block's source omitted at save time (too large to persist)
    // restores as the explanatory placeholder rather than an empty debugger.
    const source = contentRecord.sourceOmitted === true
      ? buildOmittedScriptPlaceholder()
      : typeof contentRecord.source === 'string' ? contentRecord.source : undefined;
    const defaults = createDefaultPlSqlDebuggerContent(objectName, schema, database, objectType);
    return {
      ...base,
      type: 'pl-sql-debugger',
      content: {
        ...defaults,
        // Restore anonymous-block source so the editor isn't empty on reopen.
        // For named objects, source is re-fetched from the DB on mount so we
        // leave the default `undefined` and let `loadObjectSource` populate it.
        ...(objectType === 'anonymous' && source
          ? { source, isLoading: false }
          : {}),
      },
    };
  }

  if (serialized.type === 'plsql-editor') {
    const objectName = typeof contentRecord.objectName === 'string' ? contentRecord.objectName : '';
    const schema = typeof contentRecord.schema === 'string' ? contentRecord.schema : '';
    const database = typeof contentRecord.database === 'string' ? contentRecord.database : '';
    const VALID_TYPES: PlSqlEditorObjectType[] = ['procedure', 'function', 'package', 'trigger', 'anonymous'];
    const objectType: PlSqlEditorObjectType = VALID_TYPES.includes(contentRecord.objectType as PlSqlEditorObjectType)
      ? (contentRecord.objectType as PlSqlEditorObjectType)
      : 'procedure';
    const sourceView = contentRecord.sourceView === 'spec' ? 'spec' as const : 'body' as const;
    const compiledWithDebug = contentRecord.compiledWithDebug === true;
    const memberNavOpen = contentRecord.memberNavOpen === true;

    // Restore breakpoints with safe coercion
    const VALID_HIT_COUNT_MODES: HitCountMode[] = ['=', '>=', '%'];
    const isHitCountMode = (v: unknown): v is HitCountMode =>
      typeof v === 'string' && VALID_HIT_COUNT_MODES.includes(v as HitCountMode);
    const coerceBreakpoints = (raw: unknown) => {
      if (!Array.isArray(raw)) return [];
      return raw.filter((b): b is Record<string, unknown> => isRecord(b)).map(b => ({
        line: typeof b.line === 'number' ? b.line : 0,
        enabled: b.enabled !== false,
        ...(typeof b.condition === 'string' ? { condition: b.condition } : {}),
        ...(typeof b.hitCount === 'number' ? { hitCount: b.hitCount } : {}),
        ...(isHitCountMode(b.hitCountMode) ? { hitCountMode: b.hitCountMode } : {}),
      }));
    };
    const breakpoints = coerceBreakpoints(contentRecord.breakpoints);
    const breakpointsByProgram: Record<string, Array<{ line: number; enabled: boolean; condition?: string; hitCount?: number; hitCountMode?: HitCountMode }>> = {};
    if (isRecord(contentRecord.breakpointsByProgram)) {
      for (const [key, val] of Object.entries(contentRecord.breakpointsByProgram)) {
        breakpointsByProgram[key] = coerceBreakpoints(val);
      }
    }

    // Restore watch expressions (strip runtime value/error)
    const watchExpressions = Array.isArray(contentRecord.watchExpressions)
      ? (contentRecord.watchExpressions as Array<Record<string, unknown>>)
          .filter(w => isRecord(w) && typeof w.id === 'string' && typeof w.expression === 'string')
          .map(w => ({ id: w.id as string, expression: w.expression as string }))
      : [];

    // Restore exception breakpoints
    const exceptionBreakpoints = isRecord(contentRecord.exceptionBreakpoints)
      ? {
          breakOnAll: (contentRecord.exceptionBreakpoints as Record<string, unknown>).breakOnAll === true,
          namedExceptions: isStringArray((contentRecord.exceptionBreakpoints as Record<string, unknown>).namedExceptions)
            ? (contentRecord.exceptionBreakpoints as Record<string, unknown>).namedExceptions as string[]
            : [],
        }
      : { breakOnAll: false, namedExceptions: [] as string[] };

    // Restore breakpoints from the original program's entry in breakpointsByProgram
    // to guard against serialized data from a step-into where content.breakpoints
    // belonged to a foreign program.
    const restoreNs = objectType === 'package' ? 2
      : objectType === 'trigger' ? 3
      : objectType === 'anonymous' ? 0 : 1;
    const restoreKey = `${schema.toUpperCase()}.${objectName.toUpperCase()}.${restoreNs}`;
    const restoredBreakpoints = breakpointsByProgram[restoreKey] ?? breakpoints;

    const defaults = createDefaultPlSqlEditorContent(objectName, schema, database, objectType);
    return {
      ...base,
      type: 'plsql-editor',
      content: {
        ...defaults,
        sourceView,
        compiledWithDebug,
        memberNavOpen,
        breakpoints: restoredBreakpoints,
        breakpointsByProgram,
        watchExpressions,
        exceptionBreakpoints,
      },
    };
  }

  if (serialized.type === 'job-scheduler') {
    return {
      ...base,
      type: 'job-scheduler',
      content: createDefaultJobSchedulerContent(),
    };
  }

  if (serialized.type === 'package-explorer') {
    const connectionId = typeof contentRecord.connectionId === 'string' ? contentRecord.connectionId : '';
    const database = typeof contentRecord.database === 'string' ? contentRecord.database : '';
    const searchQuery = typeof contentRecord.searchQuery === 'string' ? contentRecord.searchQuery : undefined;
    const leftPanelWidthPx = typeof contentRecord.leftPanelWidthPx === 'number' ? contentRecord.leftPanelWidthPx : undefined;
    return {
      ...base,
      type: 'package-explorer',
      content: {
        connectionId,
        database,
        searchQuery,
        leftPanelWidthPx,
      },
    };
  }

  if (serialized.type === 'dashboard') {
    const dashboardId =
      typeof contentRecord.dashboardId === 'string' ? contentRecord.dashboardId : null;
    return {
      ...base,
      type: 'dashboard',
      content: {
        ...createDefaultDashboardContent(),
        dashboardId,
      },
    };
  }

  if (serialized.type === 'references-tab') {
    const baseContent = createDefaultReferencesContent();
    const connectionId =
      typeof contentRecord.connectionId === 'string' ? contentRecord.connectionId : '';
    const database = typeof contentRecord.database === 'string' ? contentRecord.database : '';
    const engineId = (typeof contentRecord.engineId === 'string'
      ? contentRecord.engineId
      : baseContent.engineId) as DatabaseEngineId;
    const identifier = isRecord(contentRecord.identifier)
      ? (contentRecord.identifier as unknown as QualifiedName)
      : baseContent.identifier;
    const identifierKind =
      typeof contentRecord.identifierKind === 'string'
        ? (contentRecord.identifierKind as IdentifierKind)
        : baseContent.identifierKind;
    return {
      ...base,
      type: 'references-tab',
      content: {
        ...baseContent,
        connectionId,
        database,
        engineId,
        identifier,
        identifierKind,
        ...(isRecord(contentRecord.relation)
          ? { relation: contentRecord.relation as unknown as QualifiedName }
          : {}),
        // Dormant — runtime results are re-derived by re-running findReferences
        // when the user activates the tab.
        groups: [],
        bodies: {},
        status: 'idle',
      },
    };
  }

  if (serialized.type === 'database-blueprint') {
    const blueprintFolderId =
      typeof contentRecord.blueprintFolderId === 'string' ? contentRecord.blueprintFolderId : '';
    const selectedFilePath =
      typeof contentRecord.selectedFilePath === 'string' ? contentRecord.selectedFilePath : undefined;
    const selectedLinkedDbLabel =
      typeof contentRecord.selectedLinkedDbLabel === 'string'
        ? contentRecord.selectedLinkedDbLabel
        : undefined;
    const activePanel =
      contentRecord.activePanel === 'files' ||
      contentRecord.activePanel === 'git' ||
      contentRecord.activePanel === 'drift'
        ? contentRecord.activePanel
        : 'files';
    return {
      ...base,
      type: 'database-blueprint',
      content: {
        blueprintFolderId,
        selectedFilePath,
        selectedLinkedDbLabel,
        activePanel,
      },
    };
  }

  if (serialized.type === 'cross-engine-migrate') {
    const scope = contentRecord.scope === 'database' ? 'database' : 'table';
    const srcRecord = toRecord(contentRecord.source);
    const source = {
      connectionId: typeof srcRecord.connectionId === 'string' ? srcRecord.connectionId : '',
      database: typeof srcRecord.database === 'string' ? srcRecord.database : '',
      engine: typeof srcRecord.engine === 'string' ? srcRecord.engine : 'mssql',
      ...(typeof srcRecord.schema === 'string' ? { schema: srcRecord.schema } : {}),
      ...(typeof srcRecord.table === 'string' ? { table: srcRecord.table } : {}),
    };
    const tgtRecord = toRecord(contentRecord.target);
    const target =
      typeof tgtRecord.connectionId === 'string' && typeof tgtRecord.database === 'string'
        ? {
            connectionId: tgtRecord.connectionId,
            database: tgtRecord.database,
            engine: typeof tgtRecord.engine === 'string' ? tgtRecord.engine : '',
          }
        : undefined;
    const subset = Array.isArray(contentRecord.subset)
      ? (contentRecord.subset as Array<{ schema: string; table: string }>)
      : contentRecord.subset === null
        ? null
        : undefined;
    return {
      ...base,
      type: 'cross-engine-migrate',
      content: { scope, source, ...(target ? { target } : {}), ...(subset !== undefined ? { subset } : {}) },
    };
  }

  if (serialized.type === 'spatial-explorer') {
    const connectionId = typeof contentRecord.connectionId === 'string' ? contentRecord.connectionId : '';
    const databaseName = typeof contentRecord.databaseName === 'string' ? contentRecord.databaseName : '';
    const view = coerceSpatialView(contentRecord.view);

    const baseContent = createDefaultSpatialExplorerContent(connectionId, databaseName);

    return {
      ...base,
      type: 'spatial-explorer',
      content: {
        ...baseContent,
        view,
        // Restored dormant — layers reload from their table/query source on activate (T17).
        isDormant: true,
      },
    };
  }

  if (serialized.type === 'query-store') {
    // Identity + preferences; every unknown/garbage field falls back to the default.
    const defaults = createDefaultQueryStoreContent(typeof contentRecord.database === 'string' ? contentRecord.database : '');
    const sortRecord = isRecord(contentRecord.sort) ? contentRecord.sort : undefined;
    const positiveInt = coerceQueryStorePositiveInt;
    const content: QueryStoreTabContent = {
      ...defaults,
      periodHours: positiveInt(contentRecord.periodHours, defaults.periodHours),
      ...(typeof contentRecord.anchorUtc === 'string' && !Number.isNaN(Date.parse(contentRecord.anchorUtc))
        ? { anchorUtc: contentRecord.anchorUtc }
        : {}),
      includeActiveInterval: contentRecord.includeActiveInterval === true,
      metric: isQueryStoreMetric(contentRecord.metric) ? contentRecord.metric : defaults.metric,
      minExecutions: positiveInt(contentRecord.minExecutions, defaults.minExecutions),
      limit: positiveInt(contentRecord.limit, defaults.limit),
      ...(sortRecord && typeof sortRecord.id === 'string' ? { sort: { id: sortRecord.id, desc: sortRecord.desc === true } } : {}),
      ...(typeof contentRecord.selectedQueryId === 'string' && /^\d{1,20}$/.test(contentRecord.selectedQueryId)
        ? { selectedQueryId: contentRecord.selectedQueryId }
        : {}),
    };
    return { ...base, type: 'query-store', content };
  }

  if (serialized.type === 'storage-explorer') {
    return { ...base, type: 'storage-explorer', content: deserializeStorageExplorerContent(contentRecord) };
  }

  if (serialized.type === 'deadlock-analyzer') {
    // Metadata only. A pasted report was never persisted, so it restores as an
    // import-needed `paste` state; file/sample sources reload from their origin.
    const rawKind = contentRecord.sourceKind;
    const sourceKind: DeadlockAnalyzerSourceKind =
      rawKind === 'file' || rawKind === 'paste' || rawKind === 'sample' || rawKind === 'connection' ? rawKind : 'none';
    const filePath = typeof contentRecord.filePath === 'string' && contentRecord.filePath ? contentRecord.filePath : undefined;
    const fileName = typeof contentRecord.fileName === 'string' && contentRecord.fileName ? contentRecord.fileName : undefined;
    const selectedIncidentIndex =
      typeof contentRecord.selectedIncidentIndex === 'number' && contentRecord.selectedIncidentIndex >= 0
        ? contentRecord.selectedIncidentIndex
        : undefined;
    const selectedNodeId = typeof contentRecord.selectedNodeId === 'string' ? contentRecord.selectedNodeId : undefined;
    const connectionId = typeof contentRecord.connectionId === 'string' ? contentRecord.connectionId : undefined;
    const collectorRange = parseDeadlockCollectorRange(contentRecord.collectorRange);
    const collectorDatabase =
      typeof contentRecord.collectorDatabase === 'string' && contentRecord.collectorDatabase ? contentRecord.collectorDatabase : undefined;
    return {
      ...base,
      type: 'deadlock-analyzer',
      content: {
        sourceKind,
        ...(filePath ? { filePath } : {}),
        ...(fileName ? { fileName } : {}),
        ...(selectedIncidentIndex !== undefined ? { selectedIncidentIndex } : {}),
        ...(selectedNodeId ? { selectedNodeId } : {}),
        ...(connectionId ? { connectionId } : {}),
        ...(collectorRange ? { collectorRange } : {}),
        ...(collectorDatabase ? { collectorDatabase } : {}),
      },
    };
  }

  if (serialized.type === 'data-import') {
    const filePath =
      typeof contentRecord.filePath === 'string' && contentRecord.filePath.length > 0
        ? contentRecord.filePath
        : undefined;
    const parse = isRecord(contentRecord.parse) ? (contentRecord.parse as Partial<ImportParseOptions>) : undefined;

    const targetRecord = toRecord(contentRecord.target);
    const hasTargetRecord = Object.keys(targetRecord).length > 0;
    const target: ImportTargetPlan | undefined = hasTargetRecord
      ? {
          mode: targetRecord.mode === 'existing-table' ? 'existing-table' : 'new-table',
          connectionId: typeof targetRecord.connectionId === 'string' ? targetRecord.connectionId : '',
          database: typeof targetRecord.database === 'string' ? targetRecord.database : '',
          schema: typeof targetRecord.schema === 'string' ? targetRecord.schema : '',
          table: typeof targetRecord.table === 'string' ? targetRecord.table : '',
          ...(typeof targetRecord.truncateFirst === 'boolean' ? { truncateFirst: targetRecord.truncateFirst } : {}),
          ...(typeof targetRecord.addIdentityColumn === 'boolean'
            ? { addIdentityColumn: targetRecord.addIdentityColumn }
            : {}),
          ...(typeof targetRecord.identityColumnName === 'string'
            ? { identityColumnName: targetRecord.identityColumnName }
            : {}),
        }
      : undefined;

    const columnOverrides = Array.isArray(contentRecord.columnOverrides)
      ? (contentRecord.columnOverrides.filter(
          (o) => isRecord(o) && typeof o.sourceName === 'string',
        ) as DataImportColumnOverride[])
      : undefined;
    const mappings = Array.isArray(contentRecord.mappings)
      ? (contentRecord.mappings as ImportTargetMapping[])
      : undefined;
    const rowFilter = isRecord(contentRecord.rowFilter) ? (contentRecord.rowFilter as ImportRowFilter) : undefined;

    const rawStep = typeof contentRecord.step === 'string' ? contentRecord.step : 'source';
    const mappedStep = DATA_IMPORT_LEGACY_STEP_MAP[rawStep] ?? (isDataImportStepValue(rawStep) ? rawStep : 'source');
    const step = resolveDataImportStep(mappedStep, filePath, target);

    return {
      ...base,
      type: 'data-import',
      content: {
        step,
        ...(filePath ? { filePath } : {}),
        ...(parse ? { parse } : {}),
        ...(target ? { target } : {}),
        ...(columnOverrides && columnOverrides.length > 0 ? { columnOverrides } : {}),
        ...(mappings && mappings.length > 0 ? { mappings } : {}),
        ...(rowFilter ? { rowFilter } : {}),
      },
    };
  }

  // Fallback for unknown types - log warning
  if (!isUnitTest()) {
    console.warn(`[SessionStore] Unknown tab type during deserialization: ${serialized.type}`);
  }
  return {
    ...base,
    type: 'query',
    content: {
      query: '',
      resultSets: [],
      messages: [],
      results: [],
      columns: [],
      transactionMode: 'auto',
      transactionState: 'idle',
      isolationLevel: 'default',
    },
  };
}

const DATA_IMPORT_STEPS: readonly DataImportStep[] = ['source', 'target', 'columns', 'import'];

function isDataImportStepValue(value: string): value is DataImportStep {
  return (DATA_IMPORT_STEPS as readonly string[]).includes(value);
}

// Legacy step names from earlier Data Import Wizard plan revisions, mapped to
// their current-model equivalent so old persisted sessions restore sensibly.
// Only genuinely-DEAD values are remapped here — values that are still valid in
// the current `DataImportStep` union ('source' | 'target' | 'columns' | 'import')
// pass through unchanged via the caller's `isDataImportStepValue` fallback. In
// particular `'target'` is NOT remapped: Rev-9 used it for the merged
// column-mapping step, but Rev-10 reintroduced `'target'` as the live slim
// destination step, so a blanket remap would bump current-model tabs forward a
// step on restore. (The feature is unreleased — no Rev-9 session exists whose
// `'target'`-means-columns semantics need protecting.)
const DATA_IMPORT_LEGACY_STEP_MAP: Record<string, DataImportStep> = {
  'source-target': 'source',
  'settings-preview': 'source',
  run: 'import',
};

/**
 * Never restore to a Data Import step whose prerequisites aren't met — falls
 * back to the first invalid step (no file -> 'source'; incomplete destination
 * -> 'target').
 *
 * Finding #6: existing-table mode also clamps a restored 'columns'/'import' step back to 'target'.
 * The mapping + insert projection bind against `targetColumns` — the fetched target-schema snapshot
 * — which is RUNTIME-ONLY (never persisted) and re-established ONLY by a Target-step commit
 * (`commitExistingTableTarget`), not by mounting Columns or Import. So a restore straight to
 * 'columns'/'import' would leave `targetColumns` undefined: the Import Start gate unlocks once the
 * auto re-scan finishes, then sends missing target facts and fails closed. Routing back to Target
 * forces the re-commit that re-fetches the schema (and surfaces a disconnected saved connection via
 * the Target step's inline connect + footer gating — wave 1), matching the silent step-clamp
 * pattern this function already uses. New-table needs no clamp: `importDdl` re-derives each column's
 * engine-native type from its canonical kind, so a new-table run is correct without the (also
 * runtime-only) `projectedNativeTypes` — analysis auto-re-runs at the tab level and keeps Start
 * gated until it completes.
 */
function resolveDataImportStep(
  step: DataImportStep,
  filePath: string | undefined,
  target: ImportTargetPlan | undefined,
): DataImportStep {
  if (!filePath) return 'source';
  if (step === 'source') return 'source';
  const hasCompleteTarget = !!target && target.connectionId !== '' && target.database !== '' && target.table !== '';
  if (!hasCompleteTarget) return 'target';
  if (target.mode === 'existing-table') return 'target';
  return step;
}

const SECURITY_OBJECT_TYPES = [
  'login',
  'server-role',
  'user',
  'database-role',
  'schema',
  'login-role',
  'group-role',
  'mysql-user',
  'oracle-user',
  'oracle-role',
] as const satisfies readonly SecurityObjectType[];

function isSecurityObjectType(value: unknown): value is SecurityObjectType {
  return (
    typeof value === 'string' &&
    SECURITY_OBJECT_TYPES.includes(value as SecurityObjectType)
  );
}

const DEPENDENCY_OBJECT_TYPES = [
  'table',
  'view',
  'procedure',
  'function',
  'trigger',
  'index',
  'constraint',
  'synonym',
  'unknown',
] as const satisfies readonly DependencyObjectType[];

function isDependencyObjectType(value: unknown): value is DependencyObjectType {
  return (
    typeof value === 'string' &&
    DEPENDENCY_OBJECT_TYPES.includes(value as DependencyObjectType)
  );
}

const SCHEMA_FILTER_MODES = [
  'all',
  'tables',
  'views',
  'procedures',
  'functions',
] as const satisfies readonly SchemaFilterMode[];

function isSchemaFilterMode(value: unknown): value is SchemaFilterMode {
  return (
    typeof value === 'string' &&
    SCHEMA_FILTER_MODES.includes(value as SchemaFilterMode)
  );
}

const TRANSACTION_MODES = ['auto', 'manual', 'smart'] as const satisfies readonly TransactionMode[];

function isTransactionMode(value: unknown): value is TransactionMode {
  return typeof value === 'string' && TRANSACTION_MODES.includes(value as TransactionMode);
}

const TRANSACTION_ISOLATION_LEVELS = [
  'read-uncommitted', 'read-committed', 'repeatable-read', 'serializable', 'default',
] as const satisfies readonly TransactionIsolationLevel[];

function isTransactionIsolationLevel(value: unknown): value is TransactionIsolationLevel {
  return typeof value === 'string' && TRANSACTION_ISOLATION_LEVELS.includes(value as TransactionIsolationLevel);
}
